import React, { useState, useEffect } from 'react';
import { ArrowLeft, Calendar as CalendarIcon, RefreshCw, Tag, Trash, X, Send, FileEdit, Loader2, ExternalLink, Image as ImageIcon, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Keyword } from './ContentCard';
import { format, parseISO } from 'date-fns';
import { useAppState } from '@/context/AppStateContext';

// Define a proper keyword type that supports both string and object formats
interface KeywordType {
  text: string;
}

// Define a proper category type
interface Category {
  id: string;
  name: string;
}

// Type guard function to check if an object is a Category
function isCategory(obj: any): obj is Category {
  return typeof obj === 'object' && obj !== null && 'id' in obj && 'name' in obj;
}

// Type guard function to check if an object is a KeywordType
function isKeywordType(obj: any): obj is KeywordType {
  return typeof obj === 'object' && obj !== null && 'text' in obj;
}

interface ContentViewProps {
  postThemeId: string;
  title: string;
  description?: string;
  fullContent?: string;
  keywords?: Array<KeywordType | string>;
  categories?: Array<Category | string>;
  dateCreated?: string;
  status?: 'draft' | 'published' | 'scheduled' | 'content-ready';
  wpSentDate?: string;
  wpPostUrl?: string;
  preview_image_url?: string;
  onClose: () => void;
  onDeleteClick?: () => void;
  onRegenerateClick?: () => void;
  onGenerateImage?: () => void;
  onDeleteImage?: () => void;
  onSendToWordPress?: () => void;
  onGenerateAndPublish?: () => void;
  onContentUpdate?: (newContent: string) => void;
  isGeneratingContent?: boolean;
  isGeneratingImage?: boolean;
  isSendingToWP?: boolean;
  isGeneratingAndPublishing?: boolean;
  canSendToWordPress?: boolean;
  canGenerateImage?: boolean;
  onSelectImage?: (imageUrl: string) => void;
}

const ContentView: React.FC<ContentViewProps> = ({
  postThemeId,
  onClose,
}) => {
  const { setCurrentRightPaneUrl } = useAppState();

  // Set the right pane URL to show ContentEditor when component mounts
  useEffect(() => {
    console.log("📋 ContentView: Setting right pane URL", { postThemeId });
    
    if (postThemeId) {
      const url = `/content-editor/${postThemeId}`;
      console.log("📋 ContentView: Setting URL to:", url);
      setCurrentRightPaneUrl(url);
    } else {
      // If no postThemeId, close the content view
      console.log("📋 ContentView: No postThemeId, calling onClose");
      onClose();
    }
  }, [postThemeId, setCurrentRightPaneUrl, onClose]);

  // This component now just handles navigation, so return null
  return null;
};

export default ContentView;
